import {useCallback, useEffect} from "react";
import {createStore} from "@xstate/store";
import {useSelector} from "@xstate/store/react";
import {usePathname, useRouter} from "next/navigation";
import {fetchServerTime, submitUserFromWorkflow} from "@/lib/userApi";
import {logger} from "@/lib/logger";
import {useSessionGuard} from "@/lib/useSessionGuard";
import {useI18n} from "@/lib/i18n/client";
import {userMeetsMinimumAge} from "@/lib/minimumAge";
import {isValidEmail} from "@/lib/email";
import {isValidBirthdate} from "@/lib/birthdate";
import type {CalendarDate} from "@/lib/birthdate";
import type {VenueConfig} from "@/lib/venueConfig";

export enum UserFlowStage {
    Idle,
    LanguageSelection,
    BirthdateInput,
    Terms,
    NameInput,
    GenderInput,
    EmailInput,
    LifeGoalsInput,
    ThankYou
}

export type DocumentVersionRecord = {
    documentId: string;
    version: string;
    hash: string;
    language: string;
    granted: boolean;
};

export type ConsentRecord = {
    // Combined consent covering both facial photographs and voice recordings
    // (3D avatar + voice clone). Optional — declining it proceeds via the
    // reduced-experience popup.
    photoAndVoice: boolean;
    // Undefined when the Terms & Privacy checkbox was not presented to the
    // visitor (per-venue config). Distinct from `false`, which means it was
    // shown and left unchecked.
    termsAndPrivacy?: boolean;
    // Optional marketing opt-in. Undefined when the marketing checkbox was not
    // presented (per-venue config); `false` means it was shown and left
    // unchecked. Never gates Accept.
    marketing?: boolean;
    // Snapshot of the exact `{documentId, version, hash, language, granted}`
    // entries the kiosk rendered at accept time, sourced from the BM-API
    // `/legal/documents` response. Forwarded verbatim to BM API so the consent
    // audit trail can map back to the immutable archived text.
    documentVersions: DocumentVersionRecord[];
    // Server-stamped (never the kiosk clock — unreliable for legal audit)
    // when the visitor answered the consent modal. Unset if the server-time
    // fetch failed; `userDataMapper.ts` then falls back to a submit-time
    // server stamp so the audit field is never empty.
    respondedAt?: string;
};

export type User = {
    userUid?: string;
    nfcTagUid?: string;
    firstName?: string;
    lastName?: string;
    // Canonical calendar date parsed from the birthdate step's input. Stored
    // only once the typed value is a complete, valid date (see BirthdateInput);
    // the venue's date-format order lives only in that component.
    birthdate?: CalendarDate;
    // Self-declared minimum-age result, set only when the venue uses the
    // age-gate checkboxes instead of collecting a precise birthdate
    // (`collectDateOfBirth: false`). `true` = "14 or older", `false` = "under
    // 14". Undefined when a birthdate was collected instead, or before the
    // visitor has answered.
    isOfMinimumAge?: boolean;
    gender?: string;
    email?: string;
    lifeGoals?: string[];
    language?: string;
    consents?: ConsentRecord;
    // Server-stamped when the visitor tapped their badge to begin
    // registration; never set from the kiosk clock. Unset if the server-time
    // fetch failed.
    registrationStartedAt?: string;
};

export type Venue = {
    venueUid: string | null;
    urlIdentifier: string;
    displayName: string;
    supportedLanguages: string[];
    config: VenueConfig;
};

export type UserFlowState = {
    user: User;
};

// Maps a stage to its path *relative* to the venue root. Resolved paths
// are produced by `resolvePath(stage, urlIdentifier)` below.
export const stageToPathMap: Record<UserFlowStage, string> = {
    [UserFlowStage.Idle]: '',
    [UserFlowStage.LanguageSelection]: '/language',
    [UserFlowStage.BirthdateInput]: '/input-flow/birthdate',
    [UserFlowStage.Terms]: '/terms',
    [UserFlowStage.NameInput]: '/input-flow/name',
    [UserFlowStage.GenderInput]: '/input-flow/gender',
    [UserFlowStage.EmailInput]: '/input-flow/email',
    [UserFlowStage.LifeGoalsInput]: '/input-flow/life-goals',
    [UserFlowStage.ThankYou]: '/thank-you'
};

export function resolvePath(stage: UserFlowStage, urlIdentifier: string | null): string {
    if (!urlIdentifier) return '/';
    const suffix = stageToPathMap[stage];
    return `/${urlIdentifier}${suffix}`;
}

function stripVenuePrefix(pathname: string): { urlIdentifier: string | null; suffix: string } {
    if (pathname === '/' || pathname === '') return { urlIdentifier: null, suffix: '' };
    const trimmed = pathname.replace(/\/$/, '');
    const segments = trimmed.split('/').filter(Boolean);
    if (segments.length === 0) return { urlIdentifier: null, suffix: '' };
    const [identifier, ...rest] = segments;
    const suffix = rest.length === 0 ? '' : `/${rest.join('/')}`;
    return { urlIdentifier: identifier, suffix };
}

export function pathnameToStage(pathname: string): UserFlowStage {
    const { urlIdentifier, suffix } = stripVenuePrefix(pathname);
    if (!urlIdentifier) return UserFlowStage.Idle;
    if (suffix === '') return UserFlowStage.Idle;
    const match = (Object.entries(stageToPathMap) as [string, string][])
        .find(([, value]) => value === suffix);
    return match ? (Number(match[0]) as UserFlowStage) : UserFlowStage.Idle;
}

// Whether the current step's collected data is complete enough to advance.
// Derived from `user` on demand (e.g. in the input-flow layout's Next button)
// rather than stored — so re-entering a step (back navigation, refresh) always
// reflects the real data instead of a flag that was only set while typing.
// Email optionality is intentionally NOT here: it's a Skip affordance the
// layout renders separately, not a relaxation of what counts as a valid email.
export function isStageValid(stage: UserFlowStage, user: User, config?: VenueConfig): boolean {
    switch (stage) {
        case UserFlowStage.BirthdateInput:
            // In age-gate mode (collectDateOfBirth=false) the step is complete
            // once the visitor has picked one of the two checkboxes, recorded as
            // `isOfMinimumAge`. Otherwise a fully valid birthdate is required.
            if (config && !config.collectDateOfBirth) {
                return user.isOfMinimumAge !== undefined;
            }
            return isValidBirthdate(user.birthdate);
        case UserFlowStage.NameInput:
            return (user.firstName ?? '').trim().length > 0 && (user.lastName ?? '').trim().length > 0;
        case UserFlowStage.GenderInput:
            return (user.gender ?? '').length > 0;
        case UserFlowStage.EmailInput:
            return isValidEmail(user.email ?? '');
        case UserFlowStage.LifeGoalsInput:
            return (user.lifeGoals ?? []).length === 2;
        default:
            return false;
    }
}

export const store = createStore({
    context: {
        user: {} as User,
        venue: null as Venue | null,
        submissionInProgress: false,
        submissionResult: null as { success: boolean; userUid?: string; error?: unknown } | null,
        nextStage: null as UserFlowStage | null
    },
    on: {
        setVenue: (context, event: { venue: Venue }) => ({
            ...context,
            venue: event.venue
        }),
        startSession: (context, event: { user: Partial<User> }, enq) => {
            // startSession only fires from the Idle/NFC screen; the flow always
            // begins at language selection. Fixed target — not derived from a
            // stage value, which is exactly the staleness the
            // URL-as-source-of-truth refactor removes.
            const nextStage = UserFlowStage.LanguageSelection;
            const userUid = event.user.userUid;
            // Server-stamp the registration start. Fire-and-forget: a slow or
            // failed call must never block the visitor; the field stays unset.
            enq.effect(async () => {
                try {
                    const registrationStartedAt = await fetchServerTime();
                    store.trigger.registrationStartRecorded({ userUid, registrationStartedAt });
                } catch (error) {
                    logger.error('Failed to record registration start time', { error });
                }
            });
            return {
                ...context,
                user: { ...context.user, ...event.user },
                nextStage
            };
        },
        // Merge only if the session the stamp was fetched for is still active —
        // the response can land after a reset or after a different visitor
        // tapped.
        registrationStartRecorded: (context, event: { userUid?: string; registrationStartedAt: string }) =>
            context.user.userUid === event.userUid
                ? { ...context, user: { ...context.user, registrationStartedAt: event.registrationStartedAt } }
                : context,
        updateUser: (context, event: { user: Partial<User> }) => ({
            ...context,
            user: { ...context.user, ...event.user }
        }),
        acceptConsents: (context, event: { consents: ConsentRecord; email?: string }, enq) => {
            // Combined trigger: merge the consent record into the user and
            // advance to the name step in a single update so no guard fires
            // between the two state changes (would otherwise race and reset).
            // When the venue collects the email in ConsentModal
            // (askForEmailInConsent), it arrives here and is merged alongside
            // the consents.
            const nextStage = UserFlowStage.NameInput;
            const userUid = context.user.userUid;
            // Server-stamp the consent response. Fire-and-forget: a slow or
            // failed call must never block the visitor; the mapper falls back
            // to a submit-time server stamp.
            enq.effect(async () => {
                try {
                    const respondedAt = await fetchServerTime();
                    store.trigger.consentResponseRecorded({ userUid, respondedAt });
                } catch (error) {
                    logger.error('Failed to record consent response time', { error });
                }
            });
            return {
                ...context,
                user: {
                    ...context.user,
                    consents: event.consents,
                    ...(event.email !== undefined ? { email: event.email } : {})
                },
                nextStage
            };
        },
        // Merge only if the session the stamp was fetched for is still active
        // and its consent record still exists (a reset clears both).
        consentResponseRecorded: (context, event: { userUid?: string; respondedAt: string }) =>
            context.user.userUid === event.userUid && context.user.consents
                ? {
                    ...context,
                    user: {
                        ...context.user,
                        consents: { ...context.user.consents, respondedAt: event.respondedAt }
                    }
                }
                : context,
        // `from` is the stage the user is actually on, supplied by the
        // useUserFlow wrapper from the live URL — never a stored copy that a
        // browser back/forward could leave stale.
        stageCompleted: (context, event: { from: UserFlowStage }, enq) => {
            let nextStage = (event.from + 1) as UserFlowStage;
            const underMinimumAge = !userMeetsMinimumAge(context.user);

            // Visitors under 14 can't give consent themselves, so the Terms
            // step is skipped for them — they proceed straight to the name
            // step without a consent record (the submit API treats consent as
            // optional below the minimum age).
            if (nextStage === UserFlowStage.Terms && underMinimumAge) {
                nextStage = (nextStage + 1) as UserFlowStage;
            }

            // Email is only collected from users who are at least 14, and
            // venues that gather it inside ConsentModal skip the standalone
            // step entirely. Skipping here (rather than rendering an empty
            // page) keeps the flow a straight `from + 1` walk for every other
            // transition.
            const collectsEmailInConsent = context.venue?.config.askForEmailInConsent ?? false;
            if (nextStage === UserFlowStage.EmailInput && (underMinimumAge || collectsEmailInConsent)) {
                nextStage = (nextStage + 1) as UserFlowStage;
            }

            // Submit user details when reaching the final stage
            if (nextStage === UserFlowStage.ThankYou) {
                enq.effect(() => {
                    store.trigger.submitUserDetails();
                });
            }

            return {
                ...context,
                nextStage
            };
        },
        navigationCompleted: (context) => ({
            ...context,
            nextStage: null
        }),
        submitUserDetails: (context, _, enq) => {
            enq.effect(async () => {
                try {
                    const userUid = await submitUserFromWorkflow(context.user, context.venue);
                    logger.info('User data submitted successfully', { userUid });
                    store.trigger.submittingUserCompleted({ result: { success: true, userUid } });
                } catch (error) {
                    logger.error('Error submitting user data', { error });
                    store.trigger.submittingUserCompleted({
                        result: { success: false, error: error instanceof Error ? error.message : String(error) }
                    });
                }
            });

            return {
                ...context,
                submissionInProgress: true,
                submissionResult: null
            };
        },
        submittingUserCompleted: (context, event: { result: { success: boolean; userUid?: string; error?: unknown } }) => ({
            ...context,
            submissionInProgress: false,
            submissionResult: event.result
        }),
        // Prefer useUserFlow().reset over store.trigger.reset — the hook also resets the language.
        reset: (context) => ({
            user: {} as User,
            venue: context.venue,
            submissionInProgress: false,
            submissionResult: null,
            nextStage: UserFlowStage.Idle
        })
    }
});

export function useUserFlow() {
    let pathname = usePathname();
    let router = useRouter();
    let { resetLanguage } = useI18n();
    let user = useSelector(store, (state) => state.context.user);
    let venue = useSelector(store, (state) => state.context.venue);
    let stage = pathnameToStage(pathname);

    let submissionInProgress = useSelector(store, (state) => state.context.submissionInProgress);
    let submissionResult = useSelector(store, (state) => state.context.submissionResult);
    let nextStage = useSelector(store, (state) => state.context.nextStage);

    // Resets both the flow store and the language. Prefer over store.trigger.reset.
    let reset = useCallback(() => {
        resetLanguage();
        store.trigger.reset();
    }, [resetLanguage]);

    // Handle navigation when nextStage is set. Wait for the venue bundle to
    // hydrate before pushing — paths inside the preshow flow are venue-scoped,
    // so navigating with a null urlIdentifier would land on the venue picker.
    useEffect(() => {
        if (nextStage === null) return;
        if (!venue) return;

        const nextPath = resolvePath(nextStage, venue.urlIdentifier);
        if (pathname !== nextPath) {
            router.push(nextPath);
        } else {
            store.trigger.navigationCompleted();
        }
    }, [nextStage, pathname, router, venue]);

    // Guard against stale sessions after browser refresh
    // nextStage !== null means navigation is pending, so skip guards
    useSessionGuard({ stage, user, submissionResult, isNavigating: nextStage !== null, reset });

    // `stageCompleted` reads the originating stage from the live URL, never a
    // stored copy a browser back/forward could leave stale. Exposed as its own
    // function (like `reset`) so `trigger` stays the untouched store singleton.
    let stageCompleted = useCallback(
        () => store.trigger.stageCompleted({ from: pathnameToStage(pathname) }),
        [pathname]
    );

    return {
        stage,
        user,
        venue,
        submissionInProgress,
        submissionResult,
        reset,
        stageCompleted,
        trigger: store.trigger
    };
}
